<template>
  <div data-testid="pdv-table">
    <data-table
      ref="datatableRef"
      :config="datatableConfig"
      endpoint="/index.php/so/orders/lista"
      :refresh-button="refreshButtonConfig"
      @data-loaded="handleDataLoaded"
      @data-error="handleDataError">
    </data-table>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { eventBus } from '../../eventBus';
import { useSOStatusMap } from '../../composables/useSOStatusMap';
import { log } from '../../utils/DebugLogger';
import { useFiltro } from '../../composables/useFiltro';

type Id = number | string;

interface DataTableInstance {
  loadData: () => void;
}

interface OrderRow {
  so_order_id?: Id;
  so_order_client_id?: Id;
  so_order_status?: string;
  so_order_client_name?: string;
  so_order_financial_status?: string;
  so_order_total?: number;
  so_order_total_recebido?: number;
  so_order_items_count?: number;
  so_order_created_at?: string;
  franquia?: string;
  _items_count_label?: string;
  items_json?: string;
}

interface BadgeConfig {
  label: string;
  severity: string;
  tooltip?: string;
}

// open-order emit removed — OS column now opens the modal directly via eventBus
const { t, n } = useI18n();
const router = useRouter();
const { getOrderStatus, orderStatusMap } = useSOStatusMap();
const { initialized } = useFiltro();
const datatableRef = ref<DataTableInstance | null>(null);
const lastLoadedOrders = ref<OrderRow[]>([]);

const tableColumns = computed(() => [
  {
    data: '_so_order_label',
    sortField: 'so_order_id',
    title: t('OS'),
    width: 110,
    sortable: true,
    renderType: 'custom_clickable_id',
    clickCallback: (_value: unknown, rowData: Record<string, unknown>) => {
      router.push('/pdv/checkout/' + rowData.so_order_id);
    },
    mergedColumns: [
      { data: '_items_count_label', title: t('Itens: ') },
    ],
  },
  {
    data: 'so_order_status',
    title: t('Status'),
    width: 120,
    sortable: true,
    filterType: 'select',
    filterOptions: Object.entries(orderStatusMap as Record<string, { label: string }>).map(([value, { label }]) => ({ value, label })),
    renderType: 'badge',
    badgeConfig: (row: OrderRow): BadgeConfig => {
      const status = row.so_order_status;
      const mappedStatus = getOrderStatus(status);
      return {
        label: mappedStatus.label,
        severity: mappedStatus.severity,
        tooltip: mappedStatus.label
      };
    }
  },
  {
    data: 'items_json',
    title: t('Itens'),
    width: 250,
    sortable: false,
    filterType: 'text',
    cellRenderer: ({ data }: { data: OrderRow }): string => {
      if (!data.items_json) return '-';
      try {
        const items: Array<{ type: string; name: string; qty: number; priority: string; description: string }> = JSON.parse(data.items_json);
        return `<span style="display:block;line-height:1">${items
          .map(i => {
            let line = i.qty > 1 ? `${i.name} (x${i.qty})` : i.name;
            if (i.priority) line += ` ${i.priority}`;
            if (i.description) line += `<span style="display:block;font-size:0.85em;opacity:0.65;line-height:1.2">&nbsp;${i.description}</span>`;
            return line;
          })
          .join('<br>')}</span>`;
      } catch {
        return data.items_json;
      }
    }
  },
  {
    data: 'so_order_financial_status',
    title: t('Pagamento'),
    width: 110,
    sortable: true,
    filterType: 'select',
    filterOptions: [
      { value: 'a_receber', label: t('A receber') },
      { value: 'recebimento_parcial', label: t('Parcial') },
      { value: 'recebido', label: t('Recebido') },
    ],
    renderType: 'badge',
    badgeConfig: (row: OrderRow): BadgeConfig => {
      const map: Record<string, BadgeConfig> = {
        a_receber: { label: t('A receber'), severity: 'danger' },
        recebimento_parcial: { label: t('Parcial'), severity: 'warning' },
        recebido: { label: t('Recebido'), severity: 'success' }
      };
      const fs = row.so_order_financial_status;
      return (fs && map[fs]) || { label: fs || '-', severity: 'secondary' };
    }
  },
  {
    data: 'so_order_client_name',
    title: t('Cliente'),
    width: 200,
    sortable: true,
    filterType: 'text',
    renderType: 'custom_clickable_id',
    clickCallback: (value: unknown, rowData: Record<string, unknown>) => {
      const row = rowData as unknown as OrderRow;
      eventBus.emit('modal.cliente.open', { id: row.so_order_client_id });
    },
    mergedColumns: [
      { data: 'franquia', title: t('Franquia') },
    ],
  },
  {
    data: 'so_order_total',
    title: t('Total'),
    width: 160,
    sortable: true,
    filterable: true,
    filterType: 'text',
    renderType: 'currency',
    mergedColumns: [
      {
        data: 'so_order_total_recebido',
        title: t('Recebido'),
        cellRenderer: ({ data }: { data: OrderRow }): string => {
          const received = Number(data.so_order_total_recebido || 0);
          return `${t('Recebido')}: ${n(received, 'currency')}`;
        }
      },
      {
        data: '__remaining',
        title: t('Restante'),
        cellRenderer: ({ data }: { data: OrderRow }): string => {
          const total = Number(data.so_order_total || 0);
          const received = Number(data.so_order_total_recebido || 0);
          const remaining = Math.max(0, total - received);
          return `${t('Restante')}: ${n(remaining, 'currency')}`;
        }
      }
    ],
  },
  {
    data: 'so_order_created_at',
    title: t('Data'),
    width: 150,
    sortable: true,
    renderType: 'datetime'
  }
]);

const datatableConfig = computed(() => ({
  filename: `pdv_ordens_${new Date().toISOString().split('T')[0]}`,
  columns: tableColumns.value,
  defaultSortField: 'so_order_id',
  defaultSortOrder: -1,
  pageLength: 25,
  pagination: true,
  rowSelection: 'single',
  sortable: true,
  resizableColumns: true,
  showFilters: true,
  filterDisplay: 'row',
  stripedRows: true,
  showGridlines: true,
  size: 'small'
}));

const refreshButtonConfig = computed(() => ({
  show: true,
  text: t('Atualizar')
}));

const loadData = (): void => {
  log('PDVTable', 'loadData', 'Triggering table refresh');
  if (datatableRef.value) {
    datatableRef.value.loadData();
  }
};

const handleDataLoaded = (data: unknown[]): void => {
  log('PDVTable', 'handleDataLoaded', 'Data loaded', data.length, 'rows');
  const itemsPrefix = t('Itens: ');
  for (const row of data as Record<string, unknown>[]) {
    // Items count merged under OS id
    const count = (row.so_order_items_count as number | undefined) ?? 0;
    const itemsLabel = `${itemsPrefix}${count}`;
    row._items_count_label = itemsLabel;
    const osMaster = String(row.so_order_id ?? '').toLowerCase();
    row._so_order_label = `Ordem ID: ${row.so_order_id}`;
    row.__merged_so_order_id = `${osMaster} ${itemsLabel.toLowerCase()}`;
  }
  lastLoadedOrders.value = data as OrderRow[];
  eventBus.emit('pdv.orders-loaded', data);
};

const handleDataError = (err: { message?: string } | Error): void => {
  log('PDVTable', 'handleDataError', 'Error loading data', err);
  const message = err instanceof Error ? err.message : (err.message || '');
  eventBus.emit('globalMessage.error', t('Erro ao carregar ordens') + ': ' + message);
};

onMounted(() => {
  eventBus.on('filter.updated', loadData);
  eventBus.on('pdv.saved', loadData);
});

onBeforeUnmount(() => {
  eventBus.off('filter.updated', loadData);
  eventBus.off('pdv.saved', loadData);
});

defineExpose({ loadData, lastLoadedOrders });
</script>

<style scoped>
</style>
